import {
  ProgramEventSuggestionCreateParams,
  ProgramEventSuggestionsApi,
  ProgramEventSuggestion,
} from '@citruscamps/citrus-client'
import React, { useRef, useState } from 'react'
import { generateApiConfig } from '../../../utils/client-config'
import { useRequestHandler } from '../../../hooks/useRequestHandler'

interface IProps {
  onInputChange?: React.ChangeEventHandler<HTMLInputElement>
  onInputBlur?: React.FocusEventHandler<HTMLInputElement>
}

interface ISuggestedText {
  ref: React.RefObject<HTMLInputElement>
  hasSuggested: boolean
  handleSubmit: (suggestion: ProgramEventSuggestionCreateParams) => Promise<string>
}

export const useSuggestedText = ({
  onInputBlur: handleBlur,
  onInputChange: handleChange,
}: IProps): ISuggestedText => {
  const { requestHandler } = useRequestHandler()
  const ref = useRef<HTMLInputElement>(null)
  const [hasSuggested, setHasSuggested] = useState(false)
  const fetchSuggestText = async (suggestion: ProgramEventSuggestionCreateParams) => {
    if (!suggestion.program_id) {
      throw new Error('Unable to fetch data')
    }
    const client = new ProgramEventSuggestionsApi(generateApiConfig())
    const data = await requestHandler<ProgramEventSuggestion>(() =>
      client.createProgramEventSuggestion({
        program_id: suggestion.program_id,
        ProgramEventSuggestionCreateParams: {
          program_id: suggestion.program_id,
          program_event_id: suggestion?.program_event_id,
          suggestion_type: suggestion?.suggestion_type,
          prompt: suggestion?.prompt,
        },
      }),
    )
    return data?.text
  }

  const handleInputChange = async ({
    ref,
    value,
  }: {
    ref: React.RefObject<HTMLInputElement>
    value: string
  }) => {
    if (ref.current) {
      let _value = ''
      ref.current.focus()
      for (const word of value.split(' ')) {
        _value += word + ' '
        ref.current.value = _value.trim()
        handleChange?.({ target: ref.current } as any)
        handleBlur?.({ target: ref.current } as any)
        await new Promise((resolve) => setTimeout(resolve, 100))
        handleChange?.({ target: ref.current } as any)
        handleBlur?.({ target: ref.current } as any)
      }
    }
  }

  const handleSubmit = async (suggestion: ProgramEventSuggestionCreateParams) => {
    const value = await fetchSuggestText(suggestion)
    setHasSuggested(true)
    if (ref.current) handleInputChange({ ref, value })
    return value
  }

  return { ref, hasSuggested, handleSubmit }
}
